import type { Theme } from '../types/theme'

export type ThemeColorKey = keyof Theme['colors']

/** Ordered palette for multi-series / slice charts (cycles when index exceeds length). */
export function getChartPalette(theme: Theme): string[] {
  return [
    theme.colors.primary,
    theme.colors.secondary,
    theme.colors.tertiary,
    theme.colors.quaternary,
    theme.colors.success,
    theme.colors.warning,
    theme.colors.error,
    theme.colors.info,
  ]
}

export function getChartColorAt(theme: Theme, index: number): string {
  const palette = getChartPalette(theme)
  return palette[index % palette.length]
}

/** Semantic colors for positive / negative / neutral chart elements. */
export function getChartSemanticColors(theme: Theme) {
  return {
    positive: theme.colors.success,
    negative: theme.colors.error,
    neutral: theme.colors.primary,
    accent: theme.colors.info,
    warning: theme.colors.warning,
  }
}

export function getChartTitleColor(theme: Theme): string {
  return theme.colors.textSecondary
}

/** Resolve a theme color key (e.g. "primary") or pass through an explicit hex/rgb value. */
export function resolveSeriesColor(theme: Theme, color: string): string {
  if (color in theme.colors) {
    return theme.colors[color as ThemeColorKey]
  }
  return color
}

/** Append an alpha hex suffix to a #RRGGBB color (e.g. "66" ≈ 40% opacity). */
export function withColorAlpha(color: string, alphaHex: string): string {
  if (color.startsWith('#') && color.length === 7) {
    return `${color}${alphaHex}`
  }
  return color
}

/** Convert a resolved #RRGGBB color to rgba (for Sankey links, overlays, etc.). */
export function colorToRgba(color: string, alpha: number): string {
  if (color.startsWith('#') && color.length >= 7) {
    const hex = color.replace('#', '')
    const r = parseInt(hex.substring(0, 2), 16)
    const g = parseInt(hex.substring(2, 4), 16)
    const b = parseInt(hex.substring(4, 6), 16)
    return `rgba(${r}, ${g}, ${b}, ${alpha})`
  }
  if (color.startsWith('rgba(')) return color
  return color
}
